import { Apollo } from 'apollo-angular';
import { Router } from '@angular/router';
import { DocumentNode } from 'graphql';
import { AuthStore } from '@store/auth.store';
import { ExcelService } from '@services/excel.service';
import { ModuleNames } from '@enums/module-names';
import { BaseCoreArgs, BaseCoreConfig } from '@interfaces/general.interface';

export class BaseCoreUtils {
    private module: ModuleNames;
    private apollo: Apollo;
    private router: Router;
    private authStore: AuthStore;
    private excelService?: ExcelService;

    constructor(config: BaseCoreConfig) {
        this.module = config.module;
        this.apollo = config.apollo;
        this.router = config.router;
        this.authStore = config.authStore;
        this.excelService = config.excelService;
    }

    /**
     * For overview cards.
     *
     * `onError` is optional and additive: without it the behaviour is exactly
     * as before (log, or log out on 401/403). With it, a caller can render a
     * real error state instead of leaving zeroed cards on screen, which reads
     * as "nothing to report" rather than "this did not load".
     */
    overview(
      query: DocumentNode,
      args: BaseCoreArgs = {},
      callback?: (data: any) => void,
      onError?: (error: any) => void,
    ) {
      this.apollo
      .query({
          fetchPolicy: 'network-only',
          query: query,
          variables: { ...args },
      }).subscribe({
          next: (res: any) => {
              callback?.(res.data);
          },
          error: (error) => {
              this.handleError(error);
              onError?.(error);
          },
      });
    }

    //for table data (paginated from gql server)
    fetchList(
      query: DocumentNode,
      args: BaseCoreArgs = {},
      callback?: (data: any) => void,
      onError?: (error: any) => void,
    ) {
      const { page, searchQuery, sortBy, sortDirection, ...rest } = args;
      this.apollo.query({
        fetchPolicy: 'network-only',
        query: query,
        variables: {
            page: page ?? 1,
            querySearch: searchQuery ?? '',
            sortBy: sortBy
                ? { sort: sortBy, direction: sortDirection ?? 'desc' }
                : undefined,
            ...rest,
        },
      }).subscribe({
        next: (res: any) => {
            callback?.(res.data);
        },
        error: (error) => {
            this.handleError(error);
            onError?.(error);
        },
      });
    }

    //same with fetch data but with param search active
    search(query: DocumentNode, searchKeyword: string, args: BaseCoreArgs = {}, callback?: (data: any) => void) {
      this.fetchList(query, { ...args, page: 1, searchQuery: searchKeyword }, callback);
    }

   //same with fetch data but with param using active sort and page params
    sort(query: DocumentNode, sortBy: string, sortDirection: string, args: BaseCoreArgs = {}, callback?: (data: any) => void) {
      this.fetchList(query, { ...args, page: 1, sortBy, sortDirection }, callback);
    }

    downloadReport(data: any[], fileName?: string) {
      if (!this.excelService) {
          console.error('[BaseCore] ExcelService not provided');
          return;
      }
      if (!data || data.length === 0) {
          console.warn('[BaseCore] No data to export');
          return;
      }
      const reportName = fileName ?? `${this.module}_report`;
      this.excelService.generateExcel(data, reportName);
    }

    viewDetail(
      query: DocumentNode,
      args: BaseCoreArgs = {},
      callback?: (data: any) => void,
      onError?: (error: any) => void,
    ) {
      this.apollo.query({
          fetchPolicy: 'network-only',
          query: query,
          variables: { ...args },
      }).subscribe({
          next: (res: any) => {
              callback?.(res.data);
          },
          error: (error) => {
              this.handleError(error);
              onError?.(error);
          },
      });
    }

    /**
     * Create — execute a GraphQL mutation to create a record
     */
    create(mutation: DocumentNode, args: BaseCoreArgs = {}, callback?: (data: any) => void) {
        this.apollo
            .mutate({
                mutation: mutation,
                variables: { ...args },
            })
            .subscribe({
                next: (res: any) => {
                    callback?.(res.data);
                },
                error: (error) => this.handleError(error),
            });
    }

    /**
     * Update — execute a GraphQL mutation to update a record
     */
    update(mutation: DocumentNode, args: BaseCoreArgs = {}, callback?: (data: any) => void) {
        this.apollo
            .mutate({
                mutation: mutation,
                variables: { ...args },
            })
            .subscribe({
                next: (res: any) => {
                    callback?.(res.data);
                },
                error: (error) => this.handleError(error),
            });
    }

    /**
     * Centralized error handler — logs out on 401/403
     */
    private handleError(error: any) {
      if (error.message === 'Unauthorized' || error?.networkError?.status === 403) {
        this.authStore.logout().subscribe(() => {
            this.router.navigate(['/admin/login']);
        });
      } else {
        console.error(`[BaseCore][${this.module}] Error:`, error);
      }
    }
}
